/**
 * This config is used for letterboxing Madlib creatives. It works by changing the size
 * of liftoff-layout element (specific to Madlib), and scale it within liftoff-snippet
 * (provided by Liftoff.js).
 *
 * Arguably the better way to implement letterboxing is by controlling the size of
 * liftoff-snippet element based on Madlib configuration (i.e. fixed size or
 * stretchable, in the future), for example, by displaying Madlib creative in
 * an iFrame. If iFrames do not involve performance penalty, it would be
 * preferable since it would be another step to unify Madlib and CSP creatives.
 */
class LetterboxConfig {
  /**
   * @typedef {Object} Dimensions
   * @property {number} width - The width of the element.
   * @property {number} height - The height of the element.
   */

  /**
   * @typedef {Object} LetterboxConfigData
   * @property {Dimensions} viewportSize - The size of the viewport.
   * @property {Dimensions} bidCreativeSize - The size of the bid creative.
   * @property {boolean} enable - Indicates if the creative should be letterboxed.
   * @property {boolean} useDarkRootBackground - Indicates if the root element should use a dark background.
   * @property {boolean} useLayoutBackground - Indicates if the layout element should use its own light-colored background.
   */

  constructor(snippetEl) {
    this.snippetEl = snippetEl;

    /** @type {LetterboxConfigData} */
    this.config = this.getLetterboxConfig();
  }

  enabled() {
    return this.config.enable;
  }

  useDarkRootBackground() {
    return this.config.useDarkRootBackground;
  }

  useLayoutBackground() {
    return this.config.useLayoutBackground;
  }

  /**
   * Choose the appropriate config based on viewport dimensions.
   * If one orientation's config is missing, default to the other one.
   */
  getCreativePreferredOrientation({ width, height }) {
    return width <= height ? "portrait" : "landscape";
  }

  /**
   * Analyze the viewport and make some decisions about how we will present our
   * creative content.
   * @returns {LetterboxConfigData}
   */
  getLetterboxConfig() {
    const dims = (obj) => ({ width: obj.width, height: obj.height });

    const viewportSize = dims(this.snippetEl.getBoundingClientRect());
    const bidCreativeSize = dims(Liftoff.bidContext);

    const viewportOrientation =
      viewportSize.width <= viewportSize.height ? "portrait" : "landscape";

    // We are trying to show a portrait creative in a landscape-size viewport, or a landscape creative
    // in a portrait-size viewport.
    const orientationMismatch =
      viewportOrientation !==
      this.getCreativePreferredOrientation(bidCreativeSize);

    const isRotated = Liftoff.bidContext.is_interstitial && orientationMismatch;

    const useWhiteBars = isRotated;
    const useDarkRootBackground = !useWhiteBars;
    const useLayoutBackground = useDarkRootBackground;
    const enable = useWhiteBars || isRotated;

    return {
      viewportSize,
      bidCreativeSize,
      enable,
      useDarkRootBackground,
      useLayoutBackground,
    };
  }

  // Returns the scaling factor necessary to scale `contentSize` up to `viewportSize`
  // whilst maintaining the aspect ratio of the content.
  preserveAspectRatioScalingFactor(contentSize, viewportSize) {
    if (
      contentSize.height * viewportSize.width <=
      viewportSize.height * contentSize.width
    ) {
      return viewportSize.width / contentSize.width;
    } else {
      return viewportSize.height / contentSize.height;
    }
  }

  // Given a letterbox config, returns information that is useful for laying out the various DOM elements.
  // Returns an object with the following keys:
  //   scaleFactor: the scale factor to be applied to the layout element
  //   rootContainerSize: the width and height in pixels of the layout element
  //   rootContainerOffset: the x and y coordinates in pixels of the layout element
  //   rootContainerTransformedArea: expected values in pixels for the x, y, width, and height of the root
  //     container after scale and rotation are applied
  getLetterboxLayoutInfo() {
    const { viewportSize, bidCreativeSize } = this.config;

    const scaleFactor = this.preserveAspectRatioScalingFactor(
      bidCreativeSize,
      viewportSize
    );
    const rootContainerSize = bidCreativeSize;
    const rootContainerOffset = {
      x: (viewportSize.width - rootContainerSize.width) / 2,
      y: (viewportSize.height - rootContainerSize.height) / 2,
    };

    const rootContainerScaled = {
      width: bidCreativeSize.width * scaleFactor,
      height: bidCreativeSize.height * scaleFactor,
    };
    const rootContainerTransformedArea = {
      x: (viewportSize.width - rootContainerScaled.width) / 2,
      y: (viewportSize.height - rootContainerScaled.height) / 2,
      width: rootContainerScaled.width,
      height: rootContainerScaled.height,
    };

    return {
      scaleFactor,
      rootContainerSize,
      rootContainerOffset,
      rootContainerTransformedArea,
    };
  }

  getLetterboxConstraintsForMadlibRootElement() {
    const { scaleFactor, rootContainerSize, rootContainerOffset } =
      this.getLetterboxLayoutInfo();

    return {
      rootConstraints: {
        left: `${rootContainerOffset.x}px`,
        top: `${rootContainerOffset.y}px`,
        width: `${rootContainerSize.width}px`,
        height: `${rootContainerSize.height}px`,
      },
      rootPostLayoutTransform: `scale(${scaleFactor})`,
    };
  }

  getStyleForClickElement() {
    const { rootContainerTransformedArea } = this.getLetterboxLayoutInfo();
    return {
      left: `${rootContainerTransformedArea.x}px`,
      top: `${rootContainerTransformedArea.y}px`,
      width: `${rootContainerTransformedArea.width}px`,
      height: `${rootContainerTransformedArea.height}px`,
      right: "auto",
      bottom: "auto",
    };
  }
}

export default LetterboxConfig;



// WEBPACK FOOTER //
// ./src/js/madlib/letterbox_config.js